import useOfferApproval from '@/features/offers/hooks/useOfferApproval'
import useOfferGCPR from '@/features/offers/hooks/useOfferGCPR'
import useOfferPR from '@/features/offers/hooks/useOfferPR'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { hideModal } from '@/stores/ui/modalSlice'
import {
  Badge,
  Button,
  Checkbox,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react'
import { DraftSubmissionType, getPRSubmissionBlockingReasons } from '@eapproval/offer-shared'
import { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

const TYPE_LABEL = {
  [DraftSubmissionType.GCPR]: 'GC PR',
  [DraftSubmissionType.PR]: '3rd Party PR',
}

const DraftPRSubmissionModal = ({ draftSubmissions, offerId }) => {
  const dispatch = useDispatch()
  const [selectedIndexes, setSelectedIndexes] = useState([])

  const { useSubmitOfferBooking } = useOfferApproval()
  const { useSubmitGCMaterialPR } = useOfferGCPR()
  const { useSubmitPR } = useOfferPR()
  const submitOfferBooking = useSubmitOfferBooking()
  const submitGCMaterialPR = useSubmitGCMaterialPR()
  const submitPR = useSubmitPR()

  const rows = useMemo(
    () =>
      draftSubmissions.map((draft) => ({
        ...draft,
        blockingReasons: draft.type === DraftSubmissionType.PR ? getPRSubmissionBlockingReasons(draft.submission) : [],
      })),
    [draftSubmissions]
  )

  const selectableIndexes = useMemo(
    () => rows.map((row, index) => ({ row, index })).filter(({ row }) => row.blockingReasons.length === 0),
    [rows]
  )

  const handleToggleRow = (index) => {
    if (rows[index].blockingReasons.length > 0) {
      return
    }
    setSelectedIndexes((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]))
  }

  const handleToggleAll = () => {
    if (selectedIndexes.length === selectableIndexes.length) {
      setSelectedIndexes([])
    } else {
      setSelectedIndexes(selectableIndexes.map(({ index }) => index))
    }
  }

  const handleClose = () => {
    setSelectedIndexes([])
    dispatch(hideModal())
  }

  const submitDraft = async (row) => {
    if (row.type === DraftSubmissionType.GCPR) {
      return submitGCMaterialPR.mutateAsync({
        offerId,
        submissionIndex: row.submissionIndex,
      })
    }

    return submitPR.mutateAsync({
      offerId,
      costBudgetIndex: row.costBudgetIndex,
      vendorSelectionIndex: row.vendorSelectionIndex,
      submissionIndex: row.submissionIndex,
      submissionDetail: row.submission,
    })
  }

  const handleProceed = async () => {
    const selectedRows = selectedIndexes.map((index) => rows[index])

    dispatch(hideModal())
    setSelectedIndexes([])

    try {
      dispatch(showLoadingOverlay())

      await submitOfferBooking.mutateAsync({ id: offerId })

      const failed = []

      for (const row of selectedRows) {
        try {
          await submitDraft(row)
        } catch (err) {
          failed.push(row.submission?.PRNumber ?? TYPE_LABEL[row.type])
        }
      }

      if (failed.length > 0) {
        toast.error(`Booking submitted, but these PRs failed to submit: ${failed.join(', ')}`)
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to proceed with booking')
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  return (
    <Modal isOpen={true} onClose={handleClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Draft PR Submissions</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th w="40px">
                  <Checkbox
                    isChecked={selectedIndexes.length === selectableIndexes.length && selectableIndexes.length > 0}
                    isIndeterminate={selectedIndexes.length > 0 && selectedIndexes.length < selectableIndexes.length}
                    isDisabled={selectableIndexes.length === 0}
                    onChange={handleToggleAll}
                  />
                </Th>
                <Th>Type</Th>
                <Th>PR Number</Th>
                <Th isNumeric>Amount (HKD)</Th>
                <Th>Description</Th>
                <Th>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row, i) => {
                const isBlocked = row.blockingReasons.length > 0

                return (
                  <Tr
                    key={i}
                    cursor={isBlocked ? 'not-allowed' : 'pointer'}
                    bg={selectedIndexes.includes(i) ? 'blue.50' : undefined}
                    opacity={isBlocked ? 0.6 : 1}
                    onClick={() => handleToggleRow(i)}
                  >
                    <Td>
                      <Checkbox
                        isChecked={selectedIndexes.includes(i)}
                        isDisabled={isBlocked}
                        onChange={() => handleToggleRow(i)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Td>
                    <Td>
                      <Badge colorScheme={row.type === DraftSubmissionType.GCPR ? 'purple' : 'teal'}>
                        {TYPE_LABEL[row.type]}
                      </Badge>
                    </Td>
                    <Td fontWeight="medium">{row.submission?.PRNumber ?? '-'}</Td>
                    <Td isNumeric>{(row.submission?.totalAmount ?? 0).toLocaleString()}</Td>
                    <Td>{row.submission?.descriptionOfProduct || '-'}</Td>
                    <Td>
                      {isBlocked ? (
                        <Tooltip label={row.blockingReasons.join(', ')}>
                          <Text color="red.500" fontSize="xs" noOfLines={2}>
                            Not ready: {row.blockingReasons[0]}
                          </Text>
                        </Tooltip>
                      ) : (
                        <Text color="green.600" fontSize="xs">
                          Ready
                        </Text>
                      )}
                    </Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </ModalBody>
        <ModalFooter gap="2">
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button colorScheme="green" onClick={handleProceed}>
            Proceed with Booking
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default DraftPRSubmissionModal
